/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `BusinessEventCard` for the Caintra Connect frontend.
 * @module components/Events/EventListItem/BusinessEventCard
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import dayjs from "dayjs";
import { SvgIcon } from "@mui/material";
import { ReactComponent as CalendarIcon } from "../../../assets/icons/Calendar_Today.svg";
import { ReactComponent as CheckIcon } from "../../../assets/icons/Check.svg";
import { ReactComponent as SalesIcon } from "../../../assets/icons/Sales.svg";
import { EventCardShell, EventDetailColumn } from "./shared.jsx";

const BusinessEventCard = ({
  event,
  eventKey,
  t,
  styles,
  navigate,
  onRegisterB2B,
}) => (
  <EventCardShell event={event} styles={styles} eventKey={eventKey}>
    <div className={styles.eventDetails}>
      <div className={styles.businessEventDate}>
        <div className={styles.eventDates}>
          <div className={styles.businessEventDate}>
            <span className={styles.dayOfWeek}>
              {dayjs(event.fechaInicioEvento).utc().format("dddd")}
            </span>
            <span className={styles.day}>
              {dayjs(event.fechaInicioEvento).utc().format("DD")}
            </span>
            <span className={styles.monthAndYear}>
              {dayjs(event.fechaInicioEvento).utc().format("MMMM")}
              {", "}
              {dayjs(event.fechaInicioEvento).utc().format("YYYY")}
            </span>
          </div>
          <span>{t("pages.businessEvents.to")}</span>
          <div className={styles.businessEventDate}>
            <span className={styles.dayOfWeek}>
              {dayjs(event.fechaFinEvento).utc().format("dddd")}
            </span>
            <span className={styles.day}>
              {dayjs(event.fechaFinEvento).utc().format("DD")}
            </span>
            <span className={styles.monthAndYear}>
              {dayjs(event.fechaFinEvento).utc().format("MMMM")}
              {", "}
              {dayjs(event.fechaFinEvento).utc().format("YYYY")}
            </span>
          </div>
        </div>
        <button
          className={styles.registerButton}
          onClick={() =>
            event.tipoEvento === "B2B_EVENT"
              ? onRegisterB2B(event)
              : navigate(`/businessEvent/${event._id}`)
          }
        >
          {t("pages.events.viewEvent")}
        </button>
      </div>
      <div className={styles.divider} />
      <div
        className={`${styles.eventDescriptionContainer} ${
          event.preRegistro ? styles.eventDescriptionContainerWithPreRegister : ""
        }`}
      >
        <span className={styles.eventDescription}>{event.descripcion}</span>
        {event.preRegistro && (
          <div className={styles.preRegisterBlock}>
            <span className={styles.preRegisterText}>
              {event.preRegistroTexto}
            </span>
            {event.isUserRegistered ? (
              <div className={styles.preRegisterRegisteredStatus}>
                <SvgIcon
                  sx={{ fontSize: "14px", color: "#2A995A" }}
                  component={CheckIcon}
                />
                {t("pages.eventPreRegister.registered")}
              </div>
            ) : (
              <button
                type="button"
                className={styles.preRegisterButton}
                onClick={() =>
                  navigate(`/events/${event._id}/pre-register`, {
                    state: { event },
                  })
                }
              >
                {t("pages.businessEvents.register")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
    <div className={styles.eventAdditionalDetails}>
      {event.costoSocios && (
        <EventDetailColumn
          icon={SalesIcon}
          label={t("pages.businessEvents.cost")}
          value={`$ ${event.costoSocios} MXN`}
          styles={styles}
        />
      )}
      {event.fechaLimiteRegistro && (
        <EventDetailColumn
          icon={CalendarIcon}
          label={t("pages.businessEvents.dueDateRegistration")}
          value={dayjs(event.fechaLimiteRegistro).utc().format("LLL")}
          styles={styles}
        />
      )}
    </div>
  </EventCardShell>
);

export default BusinessEventCard;
